"use client";

import { useEffect, useState } from "react";
import { getUserId } from "@/lib/user/identity";
import { getAccount, getCapsForPlan } from "@/lib/account";
import { getUsageStats } from "@/lib/usage/tracking";
import { usePushNotifications } from "@/lib/hooks/usePushNotifications";
import type { Account } from "@/types/account";
import type { SubscriptionPlan } from "@/lib/subscription/plans";
import type { PlanFeatures } from "@/lib/subscription/plans";

interface AccountPanelProps {
  /** Profile plan shown when account not yet loaded (fallback) */
  profilePlan?: SubscriptionPlan;
  primaryColor: string;
  secondaryColor: string;
  textColor: string;
  textSecondaryColor: string;
}

export default function AccountPanel({
  profilePlan = "free",
  primaryColor,
  secondaryColor,
  textColor,
  textSecondaryColor,
}: AccountPanelProps) {
  const [account, setAccount] = useState<Account | null>(null);
  const { supported, permission, request, registerSync } = usePushNotifications();
  const [notificationStatus, setNotificationStatus] = useState(permission);
  const [usage, setUsage] = useState<{
    messages: number;
    apiCalls: number;
    fileUploads: number;
    knowledgeIndexes: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setNotificationStatus(permission);
  }, [permission]);

  useEffect(() => {
    async function load() {
      try {
        const userId = getUserId();
        const [acc, dayUsage] = await Promise.all([
          getAccount(userId),
          getUsageStats(userId, "day"),
        ]);
        setAccount(acc);
        setUsage(dayUsage);
      } catch (e) {
        console.error("AccountPanel load error:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading || !account) {
    return (
      <div className="p-6 rounded-2xl" style={{ color: textSecondaryColor }}>
        Loading account…
      </div>
    );
  }

  const plan: SubscriptionPlan = account.plan || profilePlan;
  const caps: PlanFeatures = getCapsForPlan(plan);

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold" style={{ color: textColor }}>
        Account
      </h2>
      <p className="text-sm" style={{ color: textSecondaryColor }}>
        One account across TD1 World, Karma AC, Realm, and Mindaloo. Limits
        apply per plan; each AI and profile uses this cap.
      </p>

      {/* Plan & limits */}
      <div
        className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm"
        style={{
          border: `1px solid ${primaryColor}20`,
          boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
        }}
      >
        <h3 className="font-bold text-xl mb-4" style={{ color: textColor }}>
          Plan & limits
        </h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between">
            <span style={{ color: textSecondaryColor }}>Plan:</span>
            <span className="font-semibold capitalize" style={{ color: textColor }}>
              {plan}
            </span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: textSecondaryColor }}>Messages/day cap:</span>
            <span style={{ color: textColor }}>
              {usage ? `${usage.messages} / ${caps.messagesPerDay}` : caps.messagesPerDay}
            </span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: textSecondaryColor }}>API calls/day cap:</span>
            <span style={{ color: textColor }}>
              {usage ? `${usage.apiCalls} / ${caps.apiCallsPerDay}` : caps.apiCallsPerDay}
            </span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: textSecondaryColor }}>File uploads/day cap:</span>
            <span style={{ color: textColor }}>
              {usage ? `${usage.fileUploads} / ${caps.fileUploadsPerDay}` : caps.fileUploadsPerDay}
            </span>
          </div>
          <div className="flex justify-between">
            <span style={{ color: textSecondaryColor }}>Media generations/month:</span>
            <span style={{ color: textColor }}>
              {caps.mediaGenerationsPerMonth === 0 ? "None on Free plan" : `up to ${caps.mediaGenerationsPerMonth}`}
            </span>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div
        className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm"
        style={{
          border: `1px solid ${primaryColor}20`,
          boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
        }}
      >
        <h3 className="font-bold text-xl mb-2" style={{ color: textColor }}>
          Notifications
        </h3>
        <p className="text-sm mb-4" style={{ color: textSecondaryColor }}>
          Get notified when the AI finishes a task, even when Mindaloo is in the background.
        </p>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="text-sm" style={{ color: textSecondaryColor }}>
            Status: <span className="font-semibold" style={{ color: textColor }}>{notificationStatus === "granted" ? "Enabled" : notificationStatus === "denied" ? "Blocked" : "Not set"}</span>
          </div>
          <button
            type="button"
            disabled={!supported || notificationStatus === "denied"}
            onClick={async () => {
              const result = await request();
              setNotificationStatus(result);
              if (result === "granted") {
                await registerSync("sync-messages");
              }
            }}
            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 text-sm text-white disabled:opacity-50 disabled:pointer-events-none"
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              boxShadow: `0 4px 16px ${primaryColor}40`,
            }}
          >
            {notificationStatus === "granted" ? "Notifications enabled" : "Enable notifications"}
          </button>
        </div>
      </div>

      {/* Pay in Karma (Karma Coin – Ethereum) */}
      <div
        className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm"
        style={{
          border: `1px solid ${primaryColor}20`,
          boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
        }}
      >
        <h3 className="font-bold text-xl mb-2" style={{ color: textColor }}>
          Pay in Karma
        </h3>
        <p className="text-sm mb-4" style={{ color: textSecondaryColor }}>
          Top up with Karma Coin (Ethereum). Wallet and currency link coming soon.
        </p>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span style={{ color: textSecondaryColor }}>Balance: </span>
            <span className="font-semibold" style={{ color: textColor }}>
              {account.karmaBalance} Karma
            </span>
          </div>
          <button
            type="button"
            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 text-sm text-white"
            style={{
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
              boxShadow: `0 4px 16px ${primaryColor}40`,
            }}
          >
            Top up (Karma Coin)
          </button>
        </div>
        <p className="text-xs mt-3" style={{ color: textSecondaryColor }}>
          Karma Coin is the ecosystem currency. Connect your wallet when the option is available.
        </p>
      </div>

      {/* Ecosystem – transferable account */}
      <div
        className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm"
        style={{
          border: `1px solid ${primaryColor}20`,
          boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
        }}
      >
        <h3 className="font-bold text-xl mb-2" style={{ color: textColor }}>
          Ecosystem account
        </h3>
        <p className="text-sm mb-4" style={{ color: textSecondaryColor }}>
          This account is designed to work across TD1 World, Karma AC, and
          Realm. Sign in with Google (when enabled) to use one identity
          everywhere; profiles and usage follow this account.
        </p>
        <ul className="text-sm space-y-1" style={{ color: textSecondaryColor }}>
          <li>• TD1 World – linked when you plug in integration</li>
          <li>• Karma AC – same account, same limits</li>
          <li>• Realm – same account, transferable</li>
        </ul>
      </div>

      <div
        className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm"
        style={{
          border: `1px solid ${primaryColor}20`,
          boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
        }}
      >
        <h3 className="font-bold text-xl mb-2" style={{ color: textColor }}>
          Legal & resources
        </h3>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm" style={{ color: textSecondaryColor }}>
          <a href="/privacy" className="hover:opacity-70 transition-opacity" style={{ color: primaryColor }}>
            Privacy
          </a>
          <span aria-hidden="true">·</span>
          <a href="/terms" className="hover:opacity-70 transition-opacity" style={{ color: primaryColor }}>
            Terms
          </a>
          <span aria-hidden="true">·</span>
          <a href="/docs" className="hover:opacity-70 transition-opacity" style={{ color: primaryColor }}>
            Docs
          </a>
          <span aria-hidden="true">·</span>
          <span>
            Built on <a href="https://karmaac.com" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: primaryColor }}>Karma AC</a> by <a href="https://td1.world" target="_blank" rel="noopener noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: primaryColor }}>TD1WORLD</a>
          </span>
        </div>
      </div>
    </div>
  );
}
